import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { userIsBuero, type CurrentUser } from "@/lib/auth";
import {
  deleteMontageunterlage,
  deleteWerkstattunterlage,
  fetchMontageAbOhnePreisePdf,
  fetchMontageDeckblattPdf,
  getMontageAbAvailability,
  fetchMontageunterlageFileBlob,
  fetchWerkstattDeckblattPdf,
  getMontageunterlagen,
  getWerkstattunterlagen,
  isMailMontageFile,
  isWerkstattPackItem,
  resolveMontageunterlageFileUrl,
  type Montageunterlage,
} from "@/lib/projectOverview";
import {
  createStuecklisteRevision,
  deleteStueckliste,
  fetchStuecklistePdf,
  listAuftragStuecklisten,
  normalizeStuecklistePack,
  setStuecklistePack,
  stuecklisteDisplayNumber,
  type Stueckliste,
} from "@/lib/stuecklisten";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import { noteNeedsReprint } from "@/projectOverview/montageNotePrintState";
import {
  COVER_PRINT_LOG_CHANGED_EVENT,
  getCoverPrintLog,
  latestCoverPrint,
} from "@/lib/coverPrintLog";
import { Lightbox } from "@/pages/material/Lightbox";
import { PaperSizeBar } from "@/components/pdf/PaperSizeBar";
import {
  documentPaperKey,
  loadDocumentPaperState,
  saveDocumentPaperState,
  setPagePaperOverride,
} from "@/lib/documentPaperOverrides";
import type { DinPaperSize } from "@/lib/paperSize";
import { MailFilePreview } from "@/projectOverview/MailFilePreview";
import { MontagePdfEmbed } from "@/projectOverview/MontagePdfEmbed";
import {
  MontageunterlagenAddDialog,
  type MontageAddMode,
} from "@/projectOverview/MontageunterlagenAddDialog";
import {
  buildDocumentPackTabs,
  montageNotesAndAddresses,
  type DocumentPackKind,
  type MontagePackTab,
} from "@/projectOverview/montageunterlagenTabs";
import { MontageunterlagenPrintDialog } from "@/projectOverview/MontageunterlagenPrintDialog";
import {
  PROJECT_OVERVIEW_NESTED_OVERLAY_Z,
  PROJECT_OVERVIEW_OVERLAY_Z,
} from "@/projectOverview/projectOverviewLayers";

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isImageContentType(contentType: string | null | undefined): boolean {
  return String(contentType || "").toLowerCase().startsWith("image/");
}

function isPdfContentType(contentType: string | null | undefined, filename?: string | null): boolean {
  const ct = String(contentType || "").toLowerCase();
  if (ct.includes("pdf")) return true;
  return String(filename || "").toLowerCase().endsWith(".pdf");
}

function canDeleteItem(
  item: Montageunterlage,
  user: CurrentUser | null,
  groups: string[],
): boolean {
  void item;
  return userCanManageMontageunterlagen(user, groups);
}

function NotesSection({
  title,
  notes,
  addresses,
  user,
  groups,
  saving,
  onEdit,
  onDelete,
  unprintedNoteIds,
}: {
  title: string;
  notes: Montageunterlage[];
  addresses: Montageunterlage[];
  user: CurrentUser | null;
  groups: string[];
  saving: boolean;
  onEdit?: (item: Montageunterlage) => void;
  onDelete?: (item: Montageunterlage) => void;
  unprintedNoteIds?: ReadonlySet<number>;
}) {
  if (!notes.length && !addresses.length) return null;
  return (
    <div className="space-y-4">
      {notes.length ? (
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            {title}-Notizen
          </h4>
          <ul className="space-y-2">
            {notes.map((item) => {
              const canDelete = onDelete && canDeleteItem(item, user, groups);
              return (
                <li
                  key={item.id}
                  className="rounded-ios border border-separator bg-fill-quaternary/40 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {item.title ? (
                          <p className="text-sm font-medium text-label">{item.title}</p>
                        ) : (
                          <p className="text-sm font-medium text-label">Notiz</p>
                        )}
                        {unprintedNoteIds?.has(item.id) ? (
                          <span className="rounded-full bg-status-red/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-red">
                            Nicht gedruckt
                          </span>
                        ) : null}
                      </div>
                      {item.body_text ? (
                        <p className="mt-0.5 whitespace-pre-wrap text-sm text-label-secondary">
                          {item.body_text}
                        </p>
                      ) : null}
                      <p className="mt-1 text-[11px] text-label-tertiary">
                        {item.created_by_display || item.created_by_login || "Unbekannt"}
                        {item.created_at ? ` · ${fmtDateTime(item.created_at)}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {onEdit ? (
                        <Button type="button" size="sm" variant="ghost" onClick={() => onEdit(item)}>
                          Bearbeiten
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={saving}
                          onClick={() => onDelete?.(item)}
                        >
                          Löschen
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {addresses.length ? (
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            {title}-Adressen
          </h4>
          <ul className="space-y-2">
            {addresses.map((item) => {
              const canDelete = onDelete && canDeleteItem(item, user, groups);
              return (
                <li
                  key={item.id}
                  className="rounded-ios border border-separator bg-fill-quaternary/40 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {item.title ? (
                        <p className="text-sm font-medium text-label">{item.title}</p>
                      ) : null}
                      {item.body_text ? (
                        <p className="mt-0.5 whitespace-pre-wrap text-sm text-label-secondary">
                          {item.body_text}
                        </p>
                      ) : null}
                      {item.maps_url ? (
                        <a
                          href={item.maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex text-xs text-accent hover:underline"
                        >
                          Google Maps
                        </a>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {onEdit ? (
                        <Button type="button" size="sm" variant="ghost" onClick={() => onEdit(item)}>
                          Bearbeiten
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={saving}
                          onClick={() => onDelete?.(item)}
                        >
                          Löschen
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function DeckblattNotesPanel({
  items,
  pack,
  user,
  groups,
  saving,
  onEdit,
  onDelete,
  unprintedNoteIds,
}: {
  items: Montageunterlage[];
  pack: DocumentPackKind;
  user: CurrentUser | null;
  groups: string[];
  saving: boolean;
  onEdit?: (item: Montageunterlage) => void;
  onDelete?: (item: Montageunterlage) => void;
  unprintedNoteIds?: ReadonlySet<number>;
}) {
  const canEditNotes = Boolean(onEdit);
  if (pack === "werkstatt") {
    const { notes, addresses } = montageNotesAndAddresses(items, "werkstatt");
    if (!notes.length && !addresses.length) {
      return (
        <p className="mt-3 text-sm text-label-tertiary">
          {canEditNotes
            ? "Noch keine Werkstatt-Notizen oder Adressen. Über „Hinzufügen“ anlegen – sie erscheinen auf dem Deckblatt und auch in den Montageunterlagen."
            : "Noch keine Werkstatt-Notizen oder Adressen hinterlegt."}
        </p>
      );
    }
    return (
      <div className="mt-3">
        <NotesSection
          title="Werkstatt"
          notes={notes}
          addresses={addresses}
          user={user}
          groups={groups}
          saving={saving}
          onEdit={onEdit}
          onDelete={onDelete}
          unprintedNoteIds={unprintedNoteIds}
        />
      </div>
    );
  }

  const montage = montageNotesAndAddresses(items, "montage");
  const werkstatt = montageNotesAndAddresses(items, "werkstatt");
  if (
    !montage.notes.length &&
    !montage.addresses.length &&
    !werkstatt.notes.length &&
    !werkstatt.addresses.length
  ) {
    return (
      <p className="mt-3 text-sm text-label-tertiary">
        {canEditNotes
          ? "Noch keine Montage-Notizen oder Adressen. Über „Hinzufügen“ anlegen – sie erscheinen auf dem Deckblatt. Werkstatt-Notizen aus den Werkstattunterlagen werden hier ebenfalls angezeigt."
          : "Noch keine Montage-Notizen oder Adressen hinterlegt. Werkstatt-Notizen aus den Werkstattunterlagen werden hier ebenfalls angezeigt."}
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-5">
      <NotesSection
        title="Montage"
        notes={montage.notes}
        addresses={montage.addresses}
        user={user}
        groups={groups}
        saving={saving}
        onEdit={onEdit}
        onDelete={onDelete}
        unprintedNoteIds={unprintedNoteIds}
      />
      <NotesSection
        title="Werkstatt"
        notes={werkstatt.notes}
        addresses={werkstatt.addresses}
        user={user}
        groups={groups}
        saving={saving}
        onEdit={undefined}
        onDelete={undefined}
        unprintedNoteIds={unprintedNoteIds}
      />
    </div>
  );
}

function ImagePaperSizeHeader({ paperKey }: { paperKey: string }) {
  const [paper, setPaper] = useState(() => {
    return (
      loadDocumentPaperState(paperKey) ?? {
        detected: ["A4" as DinPaperSize],
        overrides: {},
      }
    );
  });

  useEffect(() => {
    const stored = loadDocumentPaperState(paperKey);
    setPaper(
      stored ?? {
        detected: ["A4"],
        overrides: {},
      },
    );
  }, [paperKey]);

  useEffect(() => {
    if (!loadDocumentPaperState(paperKey)) {
      saveDocumentPaperState(paperKey, {
        detected: ["A4"],
        overrides: {},
      });
    }
  }, [paperKey]);

  return (
    <PaperSizeBar
      pageNum={1}
      pageCount={1}
      detected={paper.detected[0] ?? "A4"}
      orientation="portrait"
      value={paper.overrides[1] ?? null}
      onChange={(size) => {
        const next = setPagePaperOverride(
          {
            detected: paper.detected.length ? paper.detected : ["A4"],
            overrides: paper.overrides,
          },
          1,
          size,
        );
        setPaper(next);
        saveDocumentPaperState(paperKey, next);
      }}
    />
  );
}

function MontagePackTabContent({
  tab,
  orderId,
  pack,
  items,
  user,
  groups,
  saving,
  onEdit,
  onDelete,
  onOpenImage,
  onEditStueckliste,
  stuecklisten = [],
  onDeleteStueckliste,
  unprintedNoteIds,
}: {
  tab: MontagePackTab;
  orderId: number;
  pack: DocumentPackKind;
  items: Montageunterlage[];
  user: CurrentUser | null;
  groups: string[];
  saving: boolean;
  onEdit?: (item: Montageunterlage) => void;
  onDelete?: (item: Montageunterlage) => void;
  onOpenImage?: (item: Montageunterlage) => void;
  onEditStueckliste?: (stuecklisteId: number) => void;
  stuecklisten?: Stueckliste[];
  onDeleteStueckliste?: (stuecklisteId: number) => void;
  unprintedNoteIds?: ReadonlySet<number>;
}) {
  const paperKey = documentPaperKey(orderId, pack, tab.key);

  if (tab.kind === "deckblatt") {
    const unprintedCount = unprintedNoteIds?.size ?? 0;
    return (
      <div className="space-y-2">
        {pack === "montage" && unprintedCount > 0 ? (
          <Alert variant="warning">
            {unprintedCount === 1
              ? "Eine Notiz wurde nach dem letzten Deckblatt-Druck geändert. Bitte das Deckblatt neu drucken."
              : `${unprintedCount} Notizen wurden nach dem letzten Deckblatt-Druck geändert. Bitte das Deckblatt neu drucken.`}
          </Alert>
        ) : null}
        <MontagePdfEmbed
          loadKey={`deckblatt-${pack}-${orderId}-${items
            .filter((i) => i.entry_type === "note" || i.entry_type === "address")
            .map((i) => `${i.id}:${i.updated_at}`)
            .join("|")}`}
          loadPdf={() =>
            pack === "werkstatt"
              ? fetchWerkstattDeckblattPdf(orderId)
              : fetchMontageDeckblattPdf(orderId)
          }
          title={pack === "werkstatt" ? "Werkstatt-Deckblatt" : "Montage-Deckblatt"}
          paperKey={paperKey}
        />
        <DeckblattNotesPanel
          items={items}
          pack={pack}
          user={user}
          groups={groups}
          saving={saving}
          onEdit={onEdit}
          onDelete={onDelete}
          unprintedNoteIds={unprintedNoteIds}
        />
      </div>
    );
  }

  if (tab.kind === "ab") {
    return (
      <MontagePdfEmbed
        loadKey={`ab-${orderId}`}
        loadPdf={() => fetchMontageAbOhnePreisePdf(orderId)}
        title="Auftragsbestätigung ohne Preise"
        emptyMessage="Noch keine Auftragsbestätigung vorhanden."
        paperKey={paperKey}
      />
    );
  }

  if (tab.kind === "stueckliste") {
    const sl = stuecklisten.find((row) => row.id === tab.stuecklisteId);
    const isDraft = Boolean(sl && String(sl.status || "").toLowerCase() !== "fertig");
    return (
      <div className="space-y-2">
        {onEditStueckliste || (isDraft && onDeleteStueckliste) ? (
          <div className="flex justify-end gap-2">
            {isDraft && onDeleteStueckliste ? (
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={saving}
                onClick={() => onDeleteStueckliste(tab.stuecklisteId)}
              >
                Entwurf löschen
              </Button>
            ) : null}
            {onEditStueckliste ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={saving}
                onClick={() => onEditStueckliste(tab.stuecklisteId)}
              >
                Bearbeiten
              </Button>
            ) : null}
          </div>
        ) : null}
        <MontagePdfEmbed
          loadKey={`sl-${tab.stuecklisteId}`}
          loadPdf={() => fetchStuecklistePdf(tab.stuecklisteId)}
          title={tab.label}
          paperKey={paperKey}
        />
      </div>
    );
  }

  if (tab.kind !== "file") return null;

  const item = tab.item;
  const fileUrl = resolveMontageunterlageFileUrl(item, orderId);
  const canDelete = onDelete && canDeleteItem(item, user, groups);

  if (isImageContentType(item.content_type)) {
    return (
      <section className="min-h-[min(40vh,24rem)] rounded-xl border border-separator bg-fill-quaternary/15 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-label">{tab.label}</p>
            <p className="text-xs text-label-tertiary">
              {item.created_by_display || item.created_by_login || "Unbekannt"}
              {item.created_at ? ` · ${fmtDateTime(item.created_at)}` : ""}
            </p>
          </div>
          <div className="flex gap-1">
            {onOpenImage ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => onOpenImage(item)}>
                Vergrößern
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => onDelete?.(item)}
              >
                Löschen
              </Button>
            ) : null}
          </div>
        </div>
        <ImagePaperSizeHeader paperKey={paperKey} />
        <button
          type="button"
          className="mx-auto block max-h-[min(60vh,40rem)] overflow-hidden rounded-ios border border-separator"
          onClick={() => onOpenImage?.(item)}
        >
          <img
            src={fileUrl}
            alt={item.filename || "Bild"}
            crossOrigin="use-credentials"
            className="max-h-[min(60vh,40rem)] w-auto max-w-full object-contain"
          />
        </button>
      </section>
    );
  }

  if (isMailMontageFile(item)) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-accent hover:underline"
          >
            Original öffnen
          </a>
          {canDelete ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => onDelete?.(item)}
            >
              Löschen
            </Button>
          ) : null}
        </div>
        <MailFilePreview orderId={orderId} itemId={item.id} />
      </div>
    );
  }

  if (isPdfContentType(item.content_type, item.filename)) {
    return (
      <div className="space-y-2">
        {canDelete ? (
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => onDelete?.(item)}
            >
              Löschen
            </Button>
          </div>
        ) : null}
        <MontagePdfEmbed
          loadKey={`file-${item.id}`}
          loadPdf={() => fetchMontageunterlageFileBlob(orderId, item)}
          title={tab.label}
          paperKey={paperKey}
        />
      </div>
    );
  }

  return (
    <section className="flex min-h-[min(40vh,24rem)] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-separator bg-fill-quaternary/20 px-6 py-10 text-center">
      <p className="text-sm font-medium text-label">{tab.label}</p>
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-accent hover:underline"
      >
        Datei öffnen
      </a>
      {canDelete ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={saving}
          onClick={() => onDelete?.(item)}
        >
          Löschen
        </Button>
      ) : null}
    </section>
  );
}

export function MontageunterlagenDialog({
  open,
  orderId,
  auftragsnummer,
  user,
  groups,
  onClose,
  pack = "montage",
  initialComposerMode = null,
  initialStuecklisteId = null,
  initialTabKey = null,
  onProjectChanged,
  onStuecklisteEditorOpenChange,
}: {
  open: boolean;
  orderId: number;
  auftragsnummer?: string | null;
  user: CurrentUser | null;
  groups: string[];
  onClose: () => void;
  pack?: DocumentPackKind;
  initialComposerMode?: "stueckliste" | "note" | "address" | "file" | null;
  initialStuecklisteId?: number | null;
  /** Reiter, der beim Öffnen aktiv sein soll (z. B. `file-12` oder `sl-4`). */
  initialTabKey?: string | null;
  onProjectChanged?: () => void;
  /** true solange der Stücklisten-Editor im Add-Dialog offen ist. */
  onStuecklisteEditorOpenChange?: (open: boolean) => void;
}) {
  const [items, setItems] = useState<Montageunterlage[] | null>(null);
  const [stuecklisten, setStuecklisten] = useState<Stueckliste[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState("deckblatt");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
    contentType?: string | null;
  } | null>(null);
  const [addMode, setAddMode] = useState<MontageAddMode | null>(null);
  const [editingItem, setEditingItem] = useState<Montageunterlage | null>(null);
  const [stuecklisteAutoCreate, setStuecklisteAutoCreate] = useState(false);
  const [stuecklisteFocusId, setStuecklisteFocusId] = useState<number | null>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [hasAuftragsbestaetigung, setHasAuftragsbestaetigung] = useState(false);
  const [montagePrintedAt, setMontagePrintedAt] = useState<string | null>(null);

  const isBuero = userIsBuero(user);
  const canManage = userCanManageMontageunterlagen(user, groups);
  const isHandheld = useMediaQuery("(max-width: 1023px)");
  const packLabel = pack === "werkstatt" ? "Werkstattunterlagen" : "Montageunterlagen";

  const tabs = useMemo(
    () =>
      buildDocumentPackTabs(items ?? [], stuecklisten ?? [], pack, {
        hasAuftragsbestaetigung,
      }),
    [items, stuecklisten, pack, hasAuftragsbestaetigung],
  );
  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0] ?? null;
  const unprintedNoteIds = useMemo(() => {
    const ids = new Set<number>();
    if (pack !== "montage") return ids;
    for (const item of items ?? []) {
      if (noteNeedsReprint(item, montagePrintedAt)) ids.add(item.id);
    }
    return ids;
  }, [items, montagePrintedAt, pack]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sl, ab, prints] = await Promise.all([
        pack === "werkstatt" ? getWerkstattunterlagen(orderId) : getMontageunterlagen(orderId),
        listAuftragStuecklisten(orderId).catch(() => [] as Stueckliste[]),
        pack === "montage"
          ? getMontageAbAvailability(orderId).catch(() => ({ exists: false }))
          : Promise.resolve({ exists: false }),
        getCoverPrintLog(orderId).catch(() => []),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setStuecklisten(Array.isArray(sl) ? sl : []);
      setHasAuftragsbestaetigung(Boolean(ab?.exists));
      setMontagePrintedAt(latestCoverPrint(prints, "montage")?.created_at ?? null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `${packLabel} konnten nicht geladen werden.`,
      );
      setItems([]);
      setStuecklisten([]);
      setHasAuftragsbestaetigung(false);
      setMontagePrintedAt(null);
    } finally {
      setLoading(false);
    }
  }, [orderId, pack, packLabel]);

  useEffect(() => {
    if (!open) return;
    void reload();
  }, [open, reload]);

  const addModeRef = useRef(addMode);
  addModeRef.current = addMode;

  useEffect(() => {
    const editorOpen = open && addMode === "stueckliste";
    onStuecklisteEditorOpenChange?.(editorOpen);
    return () => {
      if (editorOpen) onStuecklisteEditorOpenChange?.(false);
    };
  }, [open, addMode, onStuecklisteEditorOpenChange]);

  useEffect(() => {
    if (!open) return;
    function onMontageChanged() {
      // Offenen Stücklisten-Editor nicht durch Voll-Reload stören.
      if (addModeRef.current === "stueckliste") return;
      void reload();
    }
    window.addEventListener("montageunterlagen_changed", onMontageChanged);
    window.addEventListener("auftraege_changed", onMontageChanged);
    window.addEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onMontageChanged);
    return () => {
      window.removeEventListener("montageunterlagen_changed", onMontageChanged);
      window.removeEventListener("auftraege_changed", onMontageChanged);
      window.removeEventListener(COVER_PRINT_LOG_CHANGED_EVENT, onMontageChanged);
    };
  }, [open, reload]);

  useEffect(() => {
    if (!open || !canManage) return;
    if (initialComposerMode === "stueckliste" || initialStuecklisteId != null) {
      setAddMode("stueckliste");
      setStuecklisteAutoCreate(initialComposerMode === "stueckliste" && initialStuecklisteId == null);
      setStuecklisteFocusId(initialStuecklisteId);
    } else if (
      initialComposerMode === "note" ||
      initialComposerMode === "address" ||
      initialComposerMode === "file"
    ) {
      setAddMode(initialComposerMode);
    }
  }, [open, canManage, initialComposerMode, initialStuecklisteId]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setActiveKey("deckblatt");
      setHasAuftragsbestaetigung(false);
      setMontagePrintedAt(null);
      setSaving(false);
      setLightbox(null);
      setAddMode(null);
      setEditingItem(null);
      setStuecklisteAutoCreate(false);
      setStuecklisteFocusId(null);
      setPrintDialogOpen(false);
      setItems(null);
      setStuecklisten(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !initialTabKey) return;
    setActiveKey(initialTabKey);
  }, [open, initialTabKey]);

  useEffect(() => {
    // Solange die Liste noch lädt, enthält `tabs` nur das Deckblatt.
    // Ein angeklicktes Dokument darf dadurch nicht wieder auf das Deckblatt springen.
    if (items === null) return;
    const first = tabs[0];
    if (!first) return;
    if (!tabs.some((tab) => tab.key === activeKey)) {
      setActiveKey(first.key);
    }
  }, [tabs, activeKey, items]);

  function openAdd(mode: MontageAddMode) {
    setEditingItem(null);
    setStuecklisteFocusId(null);
    setStuecklisteAutoCreate(mode === "stueckliste");
    setAddMode(mode);
  }

  function startEdit(item: Montageunterlage) {
    if (item.entry_type !== "note" && item.entry_type !== "address") return;
    setEditingItem(item);
    setAddMode(item.entry_type);
    setActiveKey("deckblatt");
  }

  async function handleDeleteStueckliste(stuecklisteId: number) {
    if (!canManage) return;
    const row = (stuecklisten || []).find((item) => item.id === stuecklisteId);
    if (!row || String(row.status || "").toLowerCase() === "fertig") return;
    const label = stuecklisteDisplayNumber(row);
    if (
      !window.confirm(
        `Entwurf ${label} löschen? Nur diese Stückliste wird entfernt, das Projekt bleibt erhalten.`,
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await deleteStueckliste(stuecklisteId);
      if (stuecklisteFocusId === stuecklisteId) {
        setStuecklisteFocusId(null);
        setAddMode(null);
      }
      await reload();
      onProjectChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: Montageunterlage) {
    if (!canDeleteItem(item, user, groups)) return;
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    setSaving(true);
    setError(null);
    try {
      if (isWerkstattPackItem(item) || pack === "werkstatt") {
        await deleteWerkstattunterlage(orderId, item.id);
      } else {
        await deleteMontageunterlage(orderId, item.id);
      }
      if (editingItem?.id === item.id) {
        setEditingItem(null);
        setAddMode(null);
      }
      await reload();
      onProjectChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEditStueckliste(stuecklisteId: number) {
    if (!canManage) return;
    setSaving(true);
    setError(null);
    try {
      const list = stuecklisten ?? (await listAuftragStuecklisten(orderId));
      const current = list.find((row) => row.id === stuecklisteId);
      if (!current) {
        throw new Error("Stückliste nicht gefunden.");
      }

      let targetId = current.id;
      const isFinal = String(current.status || "").toLowerCase() === "fertig";
      if (isFinal) {
        // Falls schon eine neuere Entwurfs-Revision existiert: diese öffnen.
        const baseId = current.base_stueckliste_id ?? current.id;
        const newerDraft = list
          .filter(
            (row) =>
              (row.base_stueckliste_id ?? row.id) === baseId &&
              String(row.status || "").toLowerCase() === "draft" &&
              Number(row.revision_index || 0) >= Number(current.revision_index || 0) &&
              row.id !== current.id,
          )
          .sort(
            (a, b) =>
              Number(b.revision_index || 0) - Number(a.revision_index || 0) || b.id - a.id,
          )[0];
        if (newerDraft) {
          targetId = newerDraft.id;
        } else {
          const revision = await createStuecklisteRevision(current.id);
          targetId = revision.id;
          onProjectChanged?.();
        }
      }

      // Pack aus dem geöffneten Unterlagen-Kontext übernehmen (Werkstatt vs. Montage).
      if (normalizeStuecklistePack(current.pack) !== pack) {
        await setStuecklistePack(current.id, pack);
      }
      if (targetId !== current.id) {
        await setStuecklistePack(targetId, pack);
      }

      await reload();
      setEditingItem(null);
      setStuecklisteAutoCreate(false);
      setStuecklisteFocusId(targetId);
      setAddMode("stueckliste");
      setActiveKey(`sl-${targetId}`);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Stückliste konnte nicht zur Bearbeitung geöffnet werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  const dialogTitle = auftragsnummer ? `${packLabel} · ${auftragsnummer}` : packLabel;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={dialogTitle}
        description={
          pack === "werkstatt"
            ? canManage
              ? "Unterlagen für die Werkstatt – graues Deckblatt, Stücklisten und weitere Dokumente (erscheinen auch in den Montageunterlagen)."
              : "Unterlagen für die Werkstatt (nur Ansicht)."
            : canManage
              ? "Unterlagen für die Montage – Deckblatt, Auftragsbestätigung, Stücklisten und weitere Dokumente (inkl. Werkstatt)."
              : "Unterlagen für die Montage (nur Ansicht)."
        }
        size="2xl"
        fullScreenMobile
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        dirty={addMode != null}
        bodyClassName="max-h-[min(85vh,920px)] overflow-y-auto max-sm:max-h-none max-sm:flex-1"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          {items !== null && tabs.length > 0 ? (
            <div className="min-w-0 flex-1 overflow-x-auto">
              <SegmentedControl
                name={`${packLabel} Dokumente`}
                value={activeTab?.key || "deckblatt"}
                onChange={setActiveKey}
                options={tabs.map((tab) => ({ value: tab.key, label: tab.label }))}
                className="min-w-max"
              />
            </div>
          ) : (
            <div className="min-w-0 flex-1" />
          )}

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {tabs.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setPrintDialogOpen(true)}
                disabled={saving || loading}
              >
                Drucken
              </Button>
            ) : null}
          {canManage ? (
            <Popover className="relative shrink-0">
              {({ close }) => (
                <>
                  <PopoverButton
                    type="button"
                    className="inline-flex h-8 items-center justify-center rounded-lg bg-accent px-3 text-sm font-medium text-label-on-accent shadow-sm transition hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft focus-visible:ring-offset-2"
                  >
                    Hinzufügen
                  </PopoverButton>
                  <PopoverPanel className="absolute right-0 z-[320] mt-1 w-56 rounded-ios border border-separator bg-card p-1 shadow-ios-md">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                        onClick={() => {
                          openAdd("file");
                          close();
                        }}
                      >
                        {isHandheld ? "Aus Mediathek" : "Datei vom PC"}
                      </button>
                      {isHandheld ? null : (
                        <button
                          type="button"
                          className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                          onClick={() => {
                            openAdd("mobile");
                            close();
                          }}
                        >
                          Vom Handy (QR)
                        </button>
                      )}
                      <button
                        type="button"
                        className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                        onClick={() => {
                          openAdd("project_fotos");
                          close();
                        }}
                      >
                        Projektfotos
                      </button>
                      {isBuero ? (
                        <button
                          type="button"
                          className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                          onClick={() => {
                            openAdd("sharepoint");
                            close();
                          }}
                        >
                          Aus SharePoint
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                        onClick={() => {
                          openAdd("stueckliste");
                          close();
                        }}
                      >
                        Neue Stückliste
                      </button>
                      <button
                        type="button"
                        className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                        onClick={() => {
                          openAdd("note");
                          close();
                        }}
                      >
                        Notiz
                      </button>
                      <button
                        type="button"
                        className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                        onClick={() => {
                          openAdd("address");
                          close();
                        }}
                      >
                        Adresse
                      </button>
                    </div>
                  </PopoverPanel>
                </>
              )}
            </Popover>
          ) : null}
          </div>
        </div>

        {error ? <Alert variant="error" className="mb-3">{error}</Alert> : null}

        {items === null ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
            <span className="ml-2 text-sm text-label-secondary">Lade {packLabel} …</span>
          </div>
        ) : activeTab ? (
          <MontagePackTabContent
            key={activeTab.key}
            tab={activeTab}
            orderId={orderId}
            pack={pack}
            items={items ?? []}
            user={user}
            groups={groups}
            saving={saving}
            onEdit={canManage ? startEdit : undefined}
            onDelete={canManage ? (item) => void handleDelete(item) : undefined}
            stuecklisten={stuecklisten ?? []}
            onEditStueckliste={
              canManage ? (id) => void handleEditStueckliste(id) : undefined
            }
            onDeleteStueckliste={
              canManage ? (id) => void handleDeleteStueckliste(id) : undefined
            }
            unprintedNoteIds={unprintedNoteIds}
            onOpenImage={(item) =>
              setLightbox({
                url: resolveMontageunterlageFileUrl(item, orderId),
                filename: item.filename || "bild.jpg",
                contentType: item.content_type,
              })
            }
          />
        ) : null}
      </Dialog>

      <MontageunterlagenAddDialog
        open={addMode != null}
        mode={addMode}
        orderId={orderId}
        pack={pack}
        user={user}
        groups={groups}
        editingItem={editingItem}
        initialStuecklisteId={stuecklisteFocusId}
        autoStartCreate={stuecklisteAutoCreate}
        onClose={() => {
          setAddMode(null);
          setEditingItem(null);
          setStuecklisteAutoCreate(false);
          setStuecklisteFocusId(null);
        }}
        onChanged={() => {
          void reload();
          onProjectChanged?.();
        }}
        onStuecklisteEditingIdChange={(id) => {
          setStuecklisteFocusId(id);
          if (id != null) setStuecklisteAutoCreate(false);
        }}
      />

      <Lightbox
        url={lightbox?.url ?? null}
        copySource={
          lightbox
            ? {
                url: lightbox.url,
                filename: lightbox.filename,
                contentType: lightbox.contentType || "image/jpeg",
              }
            : null
        }
        onClose={() => setLightbox(null)}
        zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      />

      <MontageunterlagenPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        orderId={orderId}
        tabs={tabs}
        pack={pack}
        zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      />
    </>
  );
}

/** Read-only Pack-Ansicht (MyDay). */
export function MontageunterlagenPackView({
  orderId,
  className,
}: {
  orderId: number;
  className?: string;
}) {
  const [items, setItems] = useState<Montageunterlage[] | null>(null);
  const [stuecklisten, setStuecklisten] = useState<Stueckliste[] | null>(null);
  const [hasAuftragsbestaetigung, setHasAuftragsbestaetigung] = useState(false);
  const [montagePrintedAt, setMontagePrintedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("deckblatt");
  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
    contentType?: string | null;
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    void Promise.all([
      getMontageunterlagen(orderId),
      listAuftragStuecklisten(orderId).catch(() => [] as Stueckliste[]),
      getMontageAbAvailability(orderId).catch(() => ({ exists: false })),
      getCoverPrintLog(orderId).catch(() => []),
    ])
      .then(([list, sl, ab, prints]) => {
        setItems(Array.isArray(list) ? list : []);
        setStuecklisten(Array.isArray(sl) ? sl : []);
        setHasAuftragsbestaetigung(Boolean(ab?.exists));
        setMontagePrintedAt(latestCoverPrint(prints, "montage")?.created_at ?? null);
      })
      .catch(() => {
        setItems([]);
        setStuecklisten([]);
        setHasAuftragsbestaetigung(false);
        setMontagePrintedAt(null);
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const tabs = useMemo(
    () =>
      buildDocumentPackTabs(items ?? [], stuecklisten ?? [], "montage", {
        hasAuftragsbestaetigung,
      }),
    [items, stuecklisten, hasAuftragsbestaetigung],
  );
  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0] ?? null;
  const unprintedNoteIds = useMemo(() => {
    const ids = new Set<number>();
    for (const item of items ?? []) {
      if (noteNeedsReprint(item, montagePrintedAt)) ids.add(item.id);
    }
    return ids;
  }, [items, montagePrintedAt]);

  useEffect(() => {
    const first = tabs[0];
    if (!first) return;
    if (!tabs.some((tab) => tab.key === activeKey)) {
      setActiveKey(first.key);
    }
  }, [tabs, activeKey]);

  if (loading && items === null) {
    return (
      <div className={clsx("flex items-center justify-center py-12", className)}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className={clsx("space-y-4", className)}>
        {tabs.length > 0 ? (
          <div className="overflow-x-auto">
            <SegmentedControl
              name="Montageunterlagen Dokumente"
              value={activeTab?.key || "deckblatt"}
              onChange={setActiveKey}
              options={tabs.map((tab) => ({ value: tab.key, label: tab.label }))}
              className="min-w-max"
            />
          </div>
        ) : null}
        {activeTab ? (
          <MontagePackTabContent
            tab={activeTab}
            orderId={orderId}
            pack="montage"
            items={items ?? []}
            user={null}
            groups={[]}
            saving={false}
            unprintedNoteIds={unprintedNoteIds}
            onOpenImage={(item) =>
              setLightbox({
                url: resolveMontageunterlageFileUrl(item, orderId),
                filename: item.filename || "bild.jpg",
                contentType: item.content_type,
              })
            }
          />
        ) : null}
      </div>
      <Lightbox
        url={lightbox?.url ?? null}
        copySource={
          lightbox
            ? {
                url: lightbox.url,
                filename: lightbox.filename,
                contentType: lightbox.contentType || "image/jpeg",
              }
            : null
        }
        onClose={() => setLightbox(null)}
        zIndexClass="z-[240]"
      />
    </>
  );
}
